<template>
  <div class="auth-container
    is-flex is-align-items-center is-justify-content-center">
    <component :is="spinner"
      v-if="$showSpinnerRequestCount > 0" />

    <!-- Fixed 350px and position: relative, matching login.css .body-container - the error
         alert hangs off its bottom edge rather than sitting inside the card. -->
    <div class="body-container">
      <div class="box login-container">
        <div class="login-logo">
          <img :src="logoImgPath"
            alt="Flexiquote Logo"
            title="Flexiquote Logo">
        </div>

        <div v-if="title"
          class="login-title">{{ title }}</div>

        <slot />
      </div>

      <div class="mobile-container has-text-centered">
        <a :href="mobileUrl">
          <i class="mdi mdi-camera"
            aria-hidden="true" />
          Mobile Image Uploader
        </a>
      </div>

      <div v-if="errorMessage && !dismissed"
        class="error-message-container"
        role="alert">
        <button type="button"
          class="error-close"
          aria-label="Close"
          @click="dismissed = true">
          <span aria-hidden="true">&times;</span>
        </button>
        <strong>Error:</strong> {{ errorMessage }}
      </div>
    </div>
  </div>
</template>

<script>
import GlobalSpinnerMixin from '@/components/mixins/global/GlobalSpinnerMixin'
import Spinner from '@/components/Spinners'

/**
 * Shared chrome for the auth pages - background, logo, card, spinner, the mobile uploader link
 * and the error alert - so SignIn and SignInCsr do not each carry their own copy of the layout.
 *
 * Follows SignIn.aspx and Common/css/login.css closely, so the two logins read as the page they
 * replace: the uploader link and the error alert sit outside the card, below it. The logo stays
 * inside the card rather than above it, which is where the legacy page puts it.
 */
export default {
  name: 'AuthCard',
  mixins: [GlobalSpinnerMixin],
  props: {
    title: {
      type: String,
      default: ''
    },
    errorMessage: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      // The legacy alert is dismissible, and a new error re-opens it.
      dismissed: false
    }
  },
  computed: {
    spinner() {
      return Spinner
    },
    mobileUrl() {
      return `${process.env.VUE_APP_ROOT_URI}/mobile`
    },
    logoImgPath() {
      return this.$theme && this.$theme.isDark
        ? require('@/assets/images/flexiquote-logo-dark.svg')
        : require('@/assets/images/flexiquote-logo-light.svg')
    }
  },
  watch: {
    errorMessage() {
      this.dismissed = false
    }
  }
}
</script>

<style lang="scss" scoped>
.auth-container {
  width: 100%;
  min-height: 100vh;
  background: url(../../assets/images/flexiquoteloginbackground.jpg) top center no-repeat !important;
  background-size: cover !important;
}

.body-container {
  position: relative;
  width: 350px;
}

.login-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1rem;
  margin-bottom: 2.5rem;

  img {
    height: 40px;
  }
}

.login-container {
  width: 100%;
  // !important to beat the global `.box { border-radius: 4px !important }` in
  // themes/shared/_shared.scss, which specificity alone cannot override.
  border-radius: 10px !important;
  padding: 1rem;
}

// Named login-title rather than title: Bulma's own .title would fight it on font-weight and
// margin, and scoped style injection order against the framework is not guaranteed.
.login-title {
  font-size: 20px;
  font-weight: 400;
  margin-bottom: 1rem;
}

.mobile-container {
  width: 100%;
  margin-top: 1rem;

  a {
    color: white;
    text-decoration: none;
    font-size: 0.9rem;
  }
}

// login.css hangs #error-alert off the bottom of .body-container, below the uploader link. The
// colours are Bootstrap 3's alert-danger, which is what the legacy page renders.
.error-message-container {
  position: absolute;
  top: 100%;
  width: 100%;
  margin-top: 20px;
  padding: 15px 35px 15px 15px;
  border: 1px solid #ebccd1;
  border-radius: 4px;
  background-color: #f2dede;
  color: #a94442;
  font-size: 0.9rem;

  // Bulma colours strong from --bulma-strong-color, which is not the alert's red.
  strong {
    color: inherit;
  }
}

.error-close {
  position: absolute;
  top: 8px;
  right: 10px;
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  font-size: 21px;
  line-height: 1;
  font-weight: bold;
  opacity: 0.2;
  cursor: pointer;

  &:hover {
    opacity: 0.5;
  }
}
</style>
